import MidiEffect from '../MidiEffect.js';
import PlaneOperations from './PlaneOperations.js';
import PlaneStats from './PlaneStats.js';
import Genetic from "./Genetic.js";
import {updatePlanarSize, WIDTH, HEIGHT, PADDING} from 'components/effects/plane/constants.js';
import {centroid} from "effects/midi/mappable/lang/geometry.js"
import NodeMessaging from "messaging/NodeMessaging.js";
import PlaneGroup from './PlaneGroup.js';
import Drawable from './Drawable.js';
import Regions from './Regions.js';
import {PARAMS_TO_NOT_MODULATE, PARAMS_TO_LOWER_DEPTH} from './getTrackParameters.js';
import ParamMappable from './ParamMappable.js';
import ParamDisableable from "effects/midi/mappable/ParamDisableable.js";
import {AbstractParameter} from 'parameters/AbstractParameter.js';
import Snapshotting from "effects/midi/mappable/Snapshotting.js";
import PlaneMacroable from './PlaneMacroable.js';
import Playable from "effects/midi/mappable/Playable.js";
import {setPlanarGradientColor, setPlanar, setEffectParameterValues} from 'actions/index.js';
import {compose} from "utils/compose.js"

// Probability that we connect an auto-mapped lfo to the lfo

const LFO_PROBABILITY = 35;

class PlaneParametersEffect extends compose(
    NodeMessaging,
    Regions,
    PlaneOperations,
    ParamDisableable,
    PlaneStats,
    ParamMappable,
    Snapshotting,
    Playable,
    PlaneMacroable,
    Genetic,
    Drawable,
)
(MidiEffect) {
    constructor(track) {
        super('Plane Parameters', track);

        if (this.track.sequencerEngine) {
            this.track.sequencerEngine.idToObject[this.id] = this;
        }

        this.newInlet("operation", (msg) => this.onOperation(msg));
        this.newInlet("speed", (msg) => this.onSpeed(msg));
        this.setupRampParameters();
        this.setupPlanarParameters();
        this.setupSnapshots();
        this.setupDrawable();
        this.setupPlayable();
        this.groups = [];

        this.counter = -1;
        this.effect = this;
        this.smokeResolution = 64;

        this.dispatch(setPlanar(this.track.id ,this));
        this.newOutlet("macros");
        this.newOutlet("smoke");
    }

    onSpeed([number]) {
        this.groups.filter(
            x => x.speedParameter.set(number));
    }

    setupPlanarParameters() {
        this.maxParametersParameter = new AbstractParameter(
            'maxParameters',
            this,
            (x,y) => [],
            {
                defaultValue: 24,
                min: 16,
                max: 64
            });

        this.registerParameter(
            this.maxParametersParameter);

        this.blurParameter = new AbstractParameter(
            'blur',
            this,
            (x,y) => [],
            {
                defaultValue: 0,
                min: 0,
                max: 40
            });

        this.registerParameter(
            this.blurParameter);

        this.splineTensionParameter = new AbstractParameter(
            'splineTension',
            this,
            (x,y) => [],
            {
                defaultValue: 1.8,
                min: 0.5,
                max: 400
            });

        this.registerParameter(
            this.splineTensionParameter);

        this.updateRateParameter = new AbstractParameter(
            'updateRate',
            this,
            (x,y) => [],
            {
                defaultValue: 0,
                min: 0,
                max: 40
            });

        this.registerParameter(
            this.updateRateParameter);

        this.setupNodeMessaging();
    }

    get splineTension() {
        return this.splineTensionParameter.get();
    }

    get maxParametersPerIter() {
        return this.maxParametersParameter.get();
    }

    get updateRate() {
        return this.updateRateParameter.get();
    }

    randomlyMapParameters(device, num, parameters) {
        parameters = super.randomlyMapParameters(device, num, parameters);

        // connect each to a lfo and set depth to random number
        for (let p of parameters) {
            continue;
            if (!p.context) {
                continue;
            }
            if (p.lfos.length === 0) {
                let lfos = this.track.lfosTrack ?
                    this.track.lfosTrack.lfos.map(x => x.lfo) :
                    this.track.sequencerEngine.lfos.map(x => x.lfo);
                if (p.context.lfoTrack) {
                    p.context.lfoTrack.lfo.isSynced = false;
                } else if (p.context.midiEffect) {
                } else if (p.isConnectedToAnyModulator()) {
                    // if its already connected to a modulator dont fuck with it
                } else if (Math.random() < LFO_PROBABILITY / 100.0) {
                    if (!PARAMS_TO_NOT_MODULATE.includes(p.name)) {
                        let lfo = choose(lfos, this);
                        p.connectToLFO(lfo);
                        p.depth = 0;
                    }
                } else {
                    p.depth = 0;
                }
            }
        }
    }

    getForces() {
        let forceXs = this.groups.flatMap(
            x => x.variables.filter(x => x.name.startsWith("force.x")));
        let forceYs = this.groups.flatMap(
            x => x.variables.filter(x => x.name.startsWith("force.y")));
        let forceDXs = this.groups.flatMap(
            x => x.variables.filter(x => x.name.startsWith("force.dx")));
        let forceDYs = this.groups.flatMap(
            x => x.variables.filter(x => x.name.startsWith("force.dy")));
        let forces = [];
        for (let i in forceXs) {
            forces.push([forceXs[i], forceYs[i], forceDXs[i], forceDYs[i]]);
        }
        return forces;
    }

    checkForces() {
        for (let [x, y, dx, dy] of this.getForces()) {
            if (x.get() >= WIDTH || x.get() + 0.25*dx.get() >= WIDTH) {
                x.set(15);
                dx.set(15);
            }
            if (y.get() >= HEIGHT || y.get() + 0.25*dy.get() >= HEIGHT) {
                y.set(15);
                dy.set(15);
            }
        }
    }


    stop() {
        // first check if WIDTH/HEIGHT fucks up the forces
        this.checkForces();

        super.stop();
        this.counter = -1;
        this.stopDrawing();
        this.groups.forEach(g => g.stop());
    }

    clear() {
        super.clear();
        this.counter = -1;
    }

    output(time, stepNumber, _stepNumber) {
        this.currentSnapshotParameter.refresh(false);
        if (!this.track.sequencerEngine.playing) {
            return this.triggers;
        }

        /*
        if (stepNumber === 0 && this.resetAtZero && this.counter !== 0) {
            this.stop();
            this._clear();
        }
        */

        let totalParameters = this.groups.length > 0 ? this.groups.map(x => x._parameters.length)
                .reduce((a,b) => a + b) :
                1;
            
        // limit the # of parameters used per group 
        let ratio = Math.min(1, this.maxParametersPerIter / totalParameters);

        this.applyPencilAutomations(stepNumber);
 
        if (this.currentPath) {
            let bulk = this.applyPath(ratio, stepNumber);
            this.track.sequencerEngine.parametersToUpdate = {
                ... this.track.sequencerEngine.parametersToUpdate,
                ... bulk
            };
            return this.triggers;
        }

        let shouldRun = stepNumber % this.updateRate === 0;
        if ((this.triggers.length === 0 && !shouldRun)) {
            return this.triggers;
        }


        if (!this.playing) {
            return this.triggers;
        }

        let bulk = {};
       
        for (let group of this.groups) {
            bulk = {
                ... bulk,
                ... group.onTrigger(_stepNumber, time, this.rampTime, ratio)
            };
        }
        this.track.sequencerEngine.parametersToUpdate = {
            ... this.track.sequencerEngine.parametersToUpdate,
            ... bulk
        };

        this.sendMacroMessages();
        this.sendMessage("smoke", this.D);
        return this.triggers;
    };

    getParameters() {
        let parameters = super.getParameters();
        for (let group of this.groups) {
            parameters = [... parameters, ... group.parameters];
        }
        return parameters;
    }

    getParameterValueToSet(i) {
        // returns the value of the parameter at index i at this time
    }

    groupParameters(parameters) {
        let group = new PlaneGroup(parameters, undefined, this.context, this.track, this);
        this.groups = [... this.groups, group];
        parameters.forEach(p => p.hasGroup = group);
    }

    ungroup(group) {
        let i = this.groups.indexOf(group);
        group._parameters.forEach(
            p => p.hasGroup = undefined);
        this.groups.splice(i, 1);
    }

    ungroupParameters(parameters) {
        let pgroup = parameters.map(p => p.hasGroup);
        parameters
            .filter(p => p.hasGroup)
            .forEach(
            p => p.hasGroup.ungroupParameter(p));

        for (let group of pgroup) {
            if (group && group._parameters.length === 0) {
                this.ungroup(group);
            }
        }
    }

    getGroupForParameter(parameter) {
        return this.groups.find(
            g => g._parameters.includes(parameter));
    }

    mergeIntoGroup(group, parameters) {
        // first ungroup all these parameters
        this.ungroupParameters(parameters);
        parameters.forEach(p => {
            group._parameters.push(p);
            p.hasGroup = group;
        });

        let toDelete = this.groups.filter(
            g => g._parameters.length === 0);
        this.groups = this.groups.filter(
            g => !toDelete.includes(g));
        this.groups.push(group);
    }

    getCopy(track, ignoreId=true) {
        let effect = new PlaneParametersEffect(track);
        effect.fromJson(this.getJson(), ignoreId);
        return effect;
    }

    get gradientColor() {
        return this.track.sequencerEngine.state.plane.gradientColors[this.id];
    }

    set gradientColor(x) {
        this.track.sequencerEngine.dispatch(
            setPlanarGradientColor(this.id, x));
    }

    getJson(isPreset) {
        return {
            ... super.getJson(isPreset),
            gradientColor: this.gradientColor,
            densities: this.getDensityJson(),
            splineTension: this.splineTension,
            width: WIDTH,
            height: HEIGHT,
            hideUI: this.hideUI,
            scriptExpanded: this.scriptExpanded && this.scriptExpanded.id,
            macros: this.getMacrosJson(),
            snapshots: this.snapshots.map(
                snap => snap.map(
                ({parameter, coordinates}) => ({
                    parameter: parameter.id,
                    coordinates
                }))),
            snapshotComponent: this.snapshotComponent,
            groups: this.groups.map(g => g.getJson())
        };
    }

    getMappableJson(isPreset) {
        return {
            parameters: this._parameters.map(x => ({
                id: x.id,
                context: !isPreset ? undefined : x.context && x.context.getJson(),
                name: !isPreset ? undefined : x.name,
                coordinates: x.getCoordinates()
            })),
            depthParameters: []
        };
    }

    unmapParameter(i) {
        let parameter = super.unmapParameter(i);
        if (parameter && parameter.hasGroup) {
            parameter.hasGroup.ungroupParameter(parameter);
        }
        if (parameter && parameter.planeMacro) {
            let macro = parameter.planeMacro;
            macro.removeParameter(parameter);
            if (macro.isEmpty()) {
                this.deleteMacro(macro);
            }
        }
    }

    fromJson(json, isCopy=false) {
        if (!isCopy) {
            this.id = json.id;
            if (this.track.sequencerEngine) {
                this.track.sequencerEngine.idToObject[this.id] = this;
            }
        }
        this.jsonSnapshots = json.snapshots;
        if (json.gradientColor) {
            this.gradientColor = json.gradientColor;
        }
        if (json.splineTension) {
            this.splineTensionParameter.set(json.splineTension);
        }

        this.hideUI = json.hideUI;

        if (json.width) {
            updatePlanarSize(json.width, json.height);
        }
        
        if (json.snapshotComponent) {
            this.snapshotComponent = json.snapshotComponent;
        }

        if (json.groups) {
            for (let _group of json.groups) {
                let group = new PlaneGroup(undefined, undefined, this.context, this.track, this);
                group.fromJson(_group);
                this.groups.push(group);
            }
        }
        this.fromMacrosJson(json.macros);

        if (json.scriptExpanded) {
            this.scriptExpanded = this.groups.find(
                ({id}) => id === json.scriptExpanded);
        }

        if (json.densities) {
            this.fromDensityJson(json.densities);
        }

        return super.fromJson(json, isCopy);
    }

    loadParameters(parameterIds, initialLoad) {
        let {lostParameters, newMappings} = super.loadParameters(parameterIds, initialLoad);
        this.groups.forEach(
            g => g.loadParameters(this._parameters, newMappings));
        this.macros.forEach(
            m => m.loadParameters(this._parameters, newMappings));

        this.snapshots = [];
        if (this.jsonSnapshots) {
            for (let snapshot of this.jsonSnapshots) {
                let _snapshot = [];
                for (let {parameter, coordinates} of snapshot) {
                    let _parameter = this._parameters.find(p => p.id === parameter);
                    if (_parameter) {
                        _snapshot.push({parameter: _parameter, coordinates});
                    }
                }
                this.snapshots.push(_snapshot);
            }
        }

        // we call stop in order to reset all the values 
        this.clear();
        this.stop();
        return {lostParameters, newMappings};
    }

        
    clearParameter(parameter, i, skipGroup) {
        super.clearParameter(parameter, i);

        this.macros.forEach(
            m => {
                m.lastHull = undefined;
                m.lastCentroid = undefined;
            });

        if (!skipGroup) {
            this.groups.forEach(
                g => g.updateParameter(parameter));
        }

        // update any on-going genetic algorithm
        if (this.selectedVariation !== undefined && !skipGroup) {
            let v = this.variations[this.selectedVariation];
            let id = parameter.id;
            if (v) {
                v[id] = parameter.getCoordinates();
            }
        }

        if (!this.setting) {
            // we are not currently setting the snapshot so
            // we set it to NO SNAPSHOT

            // basically, the user was on a snapshot an then changed
            // something 
            this.currentSnapshotParameter.set(null, true);
        }
    }
        
    replaceParameter(parameter) {
        super.replaceParameter(parameter);
        this.groups.forEach(g => g.replaceParameter(parameter));
    }

    dispose() {
        this.macros.forEach(m => m.dispose());
        this.dispatch(setPlanar(this.track.id, undefined));
        super.dispose();

    }

    onParameterUnmapped(p) {
        this.macros.filter(
            m => m.parameters.includes(p))
            .forEach(m => m.removeParameter(p));
    }

    sortParameters() {}

    shouldOutputSkip(time, stepNumber) {
        if (this.triggers.length === 0) {
            return true;
        }

        return false;
    }
}

const choose = (x) => {
    return x[Math.floor(Math.random() * x.length)];
};

export default PlaneParametersEffect;


